import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { avisar } from '../componentes/Avisos.tsx';
import { FormularioPost } from '../componentes/FormularioPost.tsx';
import { Icone } from '../componentes/Icone.tsx';
import {
  ErroDaApi,
  apiMarketing,
  type PainelDoMarketing,
  type PostDeMarketing,
  type SituacaoDoPost,
} from '../lib/api.ts';
import { nomeDoMes, paraTela, porExtenso, semanasDoMes, somarMeses } from '../lib/datas.ts';
import { SITUACOES_DO_POST, nomeDaRede, nomeDaSituacao, nomeDoFormato } from '../lib/posts.ts';
import '../estilos/sistema.css';
import '../estilos/marketing.css';

/**
 * ⭐⭐ A ABA MARKETING, PARTE 1 — o calendário editorial, 15/10/2026 (PRD §50).
 *
 * Duas vistas sobre o mesmo calendário:
 *
 * - **Mês**: a grade, com cada post no dia dele, na cor da situação e com o
 *   desenho da rede. É a pergunta *"o que sai nesta semana?"*.
 * - **Lista**: todos os posts em ordem de data, com o filtro de situação. É a
 *   pergunta *"o que ainda está em ideia?"*.
 *
 * ⭐ **O dia do post na grade é o dia em que foi ao ar**, se foi; senão, o
 * previsto (`diaNoCalendario`, calculado no servidor num lugar só).
 *
 * ⛔⛔ **A aba não toca o banco de casos** (PRD §7.1): fala só com
 * `/api/marketing`, e a regressão confere os imports desta página. ⭐ E é do
 * master: o servidor recusa o colaborador em toda rota, e o menu nem a mostra.
 */

type Vista = 'mes' | 'lista';
/** ⭐ Desde a parte 2, a janela é só a do post NOVO: o post que existe abre a ficha dele (`/marketing/:id`). */
type Janela = { data: string } | null;

/*
 * ⚠️ **O mês que se via, lembrado enquanto a aba do navegador vive.** Abrir um
 * post de novembro e voltar trazia a grade de volta para outubro, porque a
 * página nasce de novo. Fora daqui, o mês de abertura é o de hoje do servidor.
 */
let mesQueSeVia: string | null = null;

const CABECALHO_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const CHAVE_DA_VISTA = 'kairos-marketing-vista';

/* ⚠️ A vista escolhida é lembrada NESTE navegador, como o menu recolhido. Sem
   acesso ao armazenamento (janela anônima), abre no mês e funciona igual. */
function vistaLembrada(): Vista {
  try {
    return localStorage.getItem(CHAVE_DA_VISTA) === 'lista' ? 'lista' : 'mes';
  } catch {
    return 'mes';
  }
}

/** Ainda não saiu e a data prevista passou: o único caso que pede atenção. */
const passouDaData = (p: PostDeMarketing, hoje: string) => p.situacao !== 'publicado' && p.dataPrevista < hoje;

/** `terça, 20 de outubro` → `terça`. Prosa, em DM Sans. */
const diaDaSemana = (data: string) => porExtenso(data).split(',')[0];

function RedesDoPost({ post, comNome }: { post: PostDeMarketing; comNome?: boolean }) {
  return (
    <span className="mkt-redes" aria-label={post.redes.map(nomeDaRede).join(' e ')}>
      {post.redes.map((r) => (
        <span key={r} className="mkt-rede-icone" title={nomeDaRede(r)}>
          <Icone nome={r} tamanho={comNome ? 15 : 13} />
          {comNome && <span>{nomeDaRede(r)}</span>}
        </span>
      ))}
    </span>
  );
}

/** Os links do publicado, que abrem fora do Hub. O clique não abre a janela do post. */
function LinksDoPost({ post }: { post: PostDeMarketing }) {
  const links = post.redes
    .map((r) => [r, r === 'instagram' ? post.linkInstagram : post.linkLinkedin] as const)
    .filter(([, l]) => l);
  if (!links.length) return null;
  return (
    <span className="mkt-links">
      {links.map(([r, l]) => (
        <a
          key={r}
          href={l}
          target="_blank"
          rel="noopener noreferrer"
          title={`Abrir no ${nomeDaRede(r)}`}
          aria-label={`Abrir no ${nomeDaRede(r)}`}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <Icone nome="seta-fora" tamanho={12} />
        </a>
      ))}
    </span>
  );
}

export function Marketing({ aoExpirarSessao, aoAbrirPost }: { aoExpirarSessao: () => void; aoAbrirPost: (id: string) => void }) {
  const [vista, setVista] = useState<Vista>(vistaLembrada);
  const [mes, setMesDaGrade] = useState<string | null>(mesQueSeVia);
  const setMes = (m: string) => {
    mesQueSeVia = m;
    setMesDaGrade(m);
  };
  const [hoje, setHoje] = useState<string | null>(null);
  const [doMes, setDoMes] = useState<PainelDoMarketing | null>(null);
  const [lista, setLista] = useState<PainelDoMarketing | null>(null);
  const [situacao, setSituacao] = useState<SituacaoDoPost | ''>('');
  const [janela, setJanela] = useState<Janela>(null);
  const [erro, setErro] = useState<string | null>(null);

  function trocarVista(v: Vista) {
    setVista(v);
    try {
      localStorage.setItem(CHAVE_DA_VISTA, v);
    } catch {
      /* sem armazenamento, a escolha vale até fechar a aba */
    }
  }

  /*
   * ⚠️ **O mês de abertura é o de HOJE DO SERVIDOR**, nunca o do relógio do
   * navegador (CLAUDE.md). A primeira leitura existe para saber que dia é; a
   * partir dela, cada vista pede o seu recorte.
   */
  const recarregar = useCallback(async () => {
    try {
      setErro(null);
      if (vista === 'mes') {
        if (!mes) {
          const p = await apiMarketing.listar();
          setHoje(p.hoje);
          setMes(p.hoje.slice(0, 7));
          return;
        }
        const p = await apiMarketing.listar({ mes });
        setHoje(p.hoje);
        setDoMes(p);
      } else {
        const p = await apiMarketing.listar(situacao ? { situacao } : {});
        setHoje(p.hoje);
        setLista(p);
      }
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      setErro(e instanceof Error ? e.message : 'Não consegui carregar o calendário.');
    }
  }, [vista, mes, situacao, aoExpirarSessao]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const abrir = (post: PostDeMarketing) => aoAbrirPost(post.id);

  return (
    /*
      ⭐ **No desenho do sistema visual 2026**, como o Calendário e o
      Financeiro: a folha de identidade no alto, e o trabalho numa folha só.
    */
    <div className="mkt">
      <section className="s-folha s-identidade mkt-identidade">
        <div className="s-monograma mkt-monograma" aria-hidden="true">
          <Icone nome="marketing" tamanho={28} />
        </div>
        <div className="mkt-identidade-texto">
          <span className="s-sobrelinha">Gestão</span>
          <h1 className="titulo">Marketing</h1>
          <p className="mkt-apoio">O calendário editorial: o que sai no Instagram e no LinkedIn, e quando.</p>
        </div>
        <div className="mkt-acoes">
          <div className="mkt-vistas" role="tablist" aria-label="Vista">
            <button type="button" role="tab" aria-selected={vista === 'mes'} className={vista === 'mes' ? 'on' : undefined} onClick={() => trocarVista('mes')}>
              <Icone nome="calendario" tamanho={15} /> Mês
            </button>
            <button type="button" role="tab" aria-selected={vista === 'lista'} className={vista === 'lista' ? 'on' : undefined} onClick={() => trocarVista('lista')}>
              <Icone nome="relatorios" tamanho={15} /> Lista
            </button>
          </div>
          <button
            type="button"
            className="botao-primario s-botao"
            disabled={!hoje}
            onClick={() => hoje && setJanela({ data: hoje })}
          >
            <Icone nome="mais" tamanho={15} />
            Novo post
          </button>
        </div>
      </section>

      {erro && <p className="erro">{erro}</p>}

      {vista === 'mes' ? (
        /* ⚠️ Ao trocar de mês, a grade nova aparece na hora e os cartões chegam
           com a resposta: o cartão sai do dia dele, então o do mês anterior
           nunca cai na grade errada. */
        mes && hoje && doMes ? (
          <VistaDoMes
            mes={mes}
            hoje={hoje}
            painel={doMes}
            aoMudarMes={(m) => setMes(m)}
            aoNovo={(data) => setJanela({ data })}
            aoAbrir={abrir}
          />
        ) : (
          <div className="carregando">Carregando o calendário…</div>
        )
      ) : lista && hoje ? (
        <VistaDaLista painel={lista} hoje={hoje} situacao={situacao} aoFiltrar={setSituacao} aoAbrir={abrir} />
      ) : (
        <div className="carregando">Carregando a lista…</div>
      )}

      {janela && (
        <FormularioPost
          inicial={null}
          dataSugerida={janela.data}
          aoFechar={() => setJanela(null)}
          aoSalvar={async (dados) => {
            await apiMarketing.criar(dados);
            avisar('Post anotado no calendário, como ideia.');
            setJanela(null);
            await recarregar();
          }}
          aoApagar={async () => undefined}
        />
      )}
    </div>
  );
}

/** O cartão do post dentro do dia. A cor é a da situação; o desenho, o da rede. */
function CartaoDoMes({ post, hoje, aoAbrir }: { post: PostDeMarketing; hoje: string; aoAbrir: (p: PostDeMarketing) => void }) {
  const atrasado = passouDaData(post, hoje);
  return (
    <button
      type="button"
      className={`mkt-cartao mkt-sit-${post.situacao}${atrasado ? ' atrasado' : ''}`}
      onClick={() => aoAbrir(post)}
      title={`${post.titulo} · ${nomeDaSituacao(post.situacao)} · ${nomeDoFormato(post.formato)}${atrasado ? ' · passou da data prevista' : ''}`}
    >
      <span className="mkt-cartao-topo">
        <RedesDoPost post={post} />
        <span className="mkt-cartao-sit">{nomeDaSituacao(post.situacao)}</span>
        {atrasado && <Icone nome="atencao" tamanho={12} />}
      </span>
      <span className="mkt-cartao-tema">{post.titulo}</span>
    </button>
  );
}

function VistaDoMes({
  mes,
  hoje,
  painel,
  aoMudarMes,
  aoNovo,
  aoAbrir,
}: {
  mes: string;
  hoje: string;
  painel: PainelDoMarketing;
  aoMudarMes: (mes: string) => void;
  aoNovo: (data: string) => void;
  aoAbrir: (p: PostDeMarketing) => void;
}) {
  const porDia = new Map<string, PostDeMarketing[]>();
  for (const p of painel.posts) porDia.set(p.diaNoCalendario, [...(porDia.get(p.diaNoCalendario) ?? []), p]);
  const dias = [...porDia.keys()].sort();

  return (
    <section className="s-folha mkt-mes" aria-label={nomeDoMes(mes)}>
      <header className="s-folha-topo mkt-mes-topo">
        <div>
          <h2 className="s-folha-titulo mkt-mes-nome">{nomeDoMes(mes)}</h2>
          <p className="subtitulo mkt-fraco">
            {painel.total === 0 ? 'Nenhum post neste mês.' : `${painel.total} post${painel.total > 1 ? 's' : ''} neste mês.`}
          </p>
        </div>
        <div className="mkt-nav" role="group" aria-label="Mês">
          <button type="button" onClick={() => aoMudarMes(somarMeses(mes, -1))} aria-label="Mês anterior" title="Mês anterior">
            <Icone nome="avancar" tamanho={16} />
          </button>
          <button type="button" onClick={() => aoMudarMes(hoje.slice(0, 7))}>
            Este mês
          </button>
          <button type="button" onClick={() => aoMudarMes(somarMeses(mes, 1))} aria-label="Próximo mês" title="Próximo mês">
            <Icone nome="avancar" tamanho={16} />
          </button>
        </div>
      </header>

      {/* A legenda diz a cor de cada situação e quantos há no mês. O texto é
          o que carrega a informação; a cor só repete. */}
      <div className="mkt-legenda">
        {SITUACOES_DO_POST.map((s) => (
          <span key={s.id} className={`mkt-legenda-item mkt-sit-${s.id}`} title={s.ajuda}>
            <i aria-hidden="true" />
            {s.nome}
            <b className="dado">{painel.placar[s.id]}</b>
          </span>
        ))}
        <span className="mkt-legenda-redes">
          <span className="mkt-legenda-item">
            <Icone nome="instagram" tamanho={14} /> Instagram
          </span>
          <span className="mkt-legenda-item">
            <Icone nome="linkedin" tamanho={14} /> LinkedIn
          </span>
        </span>
      </div>

      {/* Computador: a grade. Rola dentro do próprio bloco, nunca a página. */}
      <div className="mkt-grade-rolagem">
        <table className="mkt-grade">
          <thead>
            <tr>
              {CABECALHO_SEMANA.map((d) => (
                <th key={d} scope="col">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {semanasDoMes(mes).map((semana, i) => (
              <tr key={i}>
                {semana.map((data, j) => (
                  <td
                    key={data ?? `vazio-${j}`}
                    className={'mkt-celula' + (data === null ? ' fora' : '') + (data === hoje ? ' hoje' : '')}
                  >
                    {data && (
                      <>
                        <button
                          type="button"
                          className="mkt-dia dado"
                          onClick={() => aoNovo(data)}
                          title="Novo post neste dia"
                          aria-label={`Novo post em ${paraTela(data)}`}
                          aria-current={data === hoje ? 'date' : undefined}
                        >
                          {Number(data.slice(8))}
                        </button>
                        {(porDia.get(data) ?? []).map((p) => (
                          <CartaoDoMes key={p.id} post={p} hoje={hoje} aoAbrir={aoAbrir} />
                        ))}
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Celular: o mesmo mês em lista. Sete colunas numa tela estreita ou
          ficam ilegíveis, ou fazem a página rolar de lado. */}
      <div className="mkt-mes-lista">
        {dias.length === 0 ? (
          <p className="mkt-vazio">Nenhum post neste mês.</p>
        ) : (
          dias.map((data) => (
            <div key={data} className="mkt-mes-lista-dia">
              <p className={'mkt-mes-lista-data' + (data === hoje ? ' hoje' : '')}>{porExtenso(data)}</p>
              {(porDia.get(data) ?? []).map((p) => (
                <CartaoDoMes key={p.id} post={p} hoje={hoje} aoAbrir={aoAbrir} />
              ))}
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function VistaDaLista({
  painel,
  hoje,
  situacao,
  aoFiltrar,
  aoAbrir,
}: {
  painel: PainelDoMarketing;
  hoje: string;
  situacao: SituacaoDoPost | '';
  aoFiltrar: (s: SituacaoDoPost | '') => void;
  aoAbrir: (p: PostDeMarketing) => void;
}) {
  /* A lista vem em ordem de data do servidor; aqui só se corta por mês. */
  const meses: Array<[string, PostDeMarketing[]]> = [];
  for (const p of painel.posts) {
    const m = p.diaNoCalendario.slice(0, 7);
    const ultimo = meses[meses.length - 1];
    if (ultimo && ultimo[0] === m) ultimo[1].push(p);
    else meses.push([m, [p]]);
  }
  const todas = Object.values(painel.placar).reduce((a, b) => a + b, 0);

  return (
    <>
      {/* ⛔ O número em cada botão é o que o clique mostra: o placar conta fora
          do filtro marcado (a lição da fila do dia, 30/09/2026). */}
      <div className="mkt-filtros" role="tablist" aria-label="Situação">
        <button type="button" role="tab" aria-selected={situacao === ''} className={situacao === '' ? 'on' : undefined} onClick={() => aoFiltrar('')}>
          <b className="dado">{todas}</b> Todas
        </button>
        {SITUACOES_DO_POST.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={situacao === s.id}
            title={s.ajuda}
            className={`mkt-sit-${s.id}${situacao === s.id ? ' on' : ''}`}
            onClick={() => aoFiltrar(situacao === s.id ? '' : s.id)}
          >
            <i aria-hidden="true" />
            <b className="dado">{painel.placar[s.id]}</b> {s.nome}
          </button>
        ))}
      </div>

      <section className="s-folha mkt-lista">
        {painel.posts.length === 0 ? (
          <p className="mkt-vazio">Nenhum post nesta vista.</p>
        ) : (
          <div className="mkt-tabela-fora">
            <table className="mkt-tabela">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Tema</th>
                  <th scope="col">Redes</th>
                  <th scope="col">Formato</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              {meses.map(([m, posts]) => (
                <tbody key={m}>
                  <tr className="mkt-tabela-mes">
                    <th scope="rowgroup" colSpan={5}>
                      {nomeDoMes(m)}
                    </th>
                  </tr>
                  {posts.map((p) => {
                    const atrasado = passouDaData(p, hoje);
                    return (
                      <tr
                        key={p.id}
                        tabIndex={0}
                        onClick={() => aoAbrir(p)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            aoAbrir(p);
                          }
                        }}
                      >
                        <td className={'mkt-quando' + (atrasado ? ' atrasado' : '')}>
                          <span className="dado">{paraTela(p.diaNoCalendario)}</span>
                          <small>{diaDaSemana(p.diaNoCalendario)}</small>
                          {p.publicadoEm && p.publicadoEm !== p.dataPrevista && (
                            <small>previsto para {paraTela(p.dataPrevista)}</small>
                          )}
                          {atrasado && <small>passou da data prevista</small>}
                        </td>
                        <th scope="row" className="mkt-tema">
                          <span>{p.titulo}</span>
                          {p.observacao && <small>{p.observacao}</small>}
                        </th>
                        <td>
                          <RedesDoPost post={p} comNome />
                        </td>
                        <td className="mkt-formato">{nomeDoFormato(p.formato)}</td>
                        <td>
                          <span className={`mkt-chip mkt-sit-${p.situacao}`}>
                            <i aria-hidden="true" />
                            {nomeDaSituacao(p.situacao)}
                          </span>
                          <LinksDoPost post={p} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </section>
    </>
  );
}
